import { DivisionIllustration, EmptyState, Tooltip } from '@bp/ui-components';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import { useTranslation } from 'react-i18next';
import { lowercaseFirstLetter } from 'utils/stringHelper';
import styles from './DivisionsList.module.scss';

export const DivisionsListFieldsDocument = graphql(`
  fragment DivisionsListFields on Query {
    divisionList: classes(where: { organization: { uuid: $organizationUuid } }, options: { sort: [{ name: ASC }] }) {
      uuid
      __typename
      divisions {
        uuid
        __typename
        name
        groups {
          uuid
          __typename
          name
          shortName
          division {
            uuid
            __typename
          }
          members {
            uuid
            __typename
          }
        }
      }
    }
  }
`);

type DivisionsListProps = {
  bpClass: { uuid: string; profileUuids: string[] };
  query: FragmentType<typeof DivisionsListFieldsDocument> | undefined;
};

type DivisionsListType = {
  uuid: string;
  name: string;
  totalCount: number;
  groups: {
    uuid: string;
    name: string;
    shortName: string;
    studentCount: number;
  }[];
};

export const DivisionsList = ({ bpClass, query }: DivisionsListProps) => {
  const { t } = useTranslation();
  const divisions = getFragmentData(DivisionsListFieldsDocument, query)
    ?.divisionList.filter((d) => d.uuid === bpClass.uuid)
    .flatMap((d) => d.divisions);

  const divisionsList: DivisionsListType[] =
    divisions?.map((division) => {
      return {
        uuid: division.uuid,
        name: division.name,
        totalCount: division.groups.reduce((acc, curr) => {
          return acc + curr.members.length;
        }, 0),
        groups:
          division.groups.map((g) => {
            return {
              uuid: g.uuid,
              name: g.name,
              shortName: g.shortName,
              studentCount: g.members.length,
            };
          }) ?? [],
      };
    }) ?? [];

  return (
    <div className={styles['divisions-list']}>
      {divisionsList.length === 0 ? (
        <EmptyState
          borderRadius='none'
          size='small'
          icon={<DivisionIllustration />}
          title={t('divisions.noDivisions')}
        />
      ) : (
        divisionsList.map((division) => {
          const missing = bpClass.profileUuids.length - division.totalCount;

          return (
            <div key={division.uuid} className={styles['list-item']}>
              <div className={styles.groups}>
                {division.groups.map((group) => {
                  return (
                    <div
                      key={group.uuid}
                      className={styles.group}
                      style={{ flexBasis: `${100 / division.groups.length}%` }}
                    >
                      <Tooltip
                        content={`${group.name} - ${t('classes.studentCount', { count: group.studentCount })}`}
                        triggerClass={styles.name}
                      >
                        <div className={styles['short-name']}>{group.shortName}</div>
                        <div className={styles.count}>({group.studentCount})</div>
                      </Tooltip>
                    </div>
                  );
                })}
              </div>
              <div className={styles['division-name']}>
                <div className={styles.name}>{division.name}</div>
                <div className={styles.count}>
                  {missing > 0
                    ? t('classes.missingCount', { count: missing })
                    : `${division.totalCount} ${lowercaseFirstLetter(t('common.from'))} ${bpClass.profileUuids.length} ${lowercaseFirstLetter(t('classes.assigned'))}`}
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};
